import { apiClient } from "@/lib/api-client";

/**
 * Media file metadata for upload
 */
export interface FileMetadata {
  fileName: string;
  contentType: string;
  virtualPath: string;
  tags: Record<string, string>;
}

/**
 * Media item response from API
 */
export interface MediaItem {
  url: string;
  /** Path relative to the account's media root (e.g. "images/logo_ab12cd34.png"); used for deletion. */
  path: string;
  fileName: string;
  contentType: string;
  virtualPath: string;
  tags: Record<string, string>;
  uploadDate?: string;
  size?: number;
}

/**
 * Media API Service
 * Handles all API calls for media management
 */
export class MediaService {
  private basePath = "/media";

  /**
   * Get all media items
   */
  async getMedia(): Promise<MediaItem[]> {
    return apiClient.get<MediaItem[]>(this.basePath, {
      requiresAuth: true,
    });
  }

  /**
   * Upload a single media file
   */
  async uploadMedia(file: File): Promise<MediaItem> {
    const formData = new FormData();
    
    // Generate random 8 character suffix
    const randomSuffix = this.generateRandomSuffix();
    const fileNameParts = file.name.split('.');
    const extension = fileNameParts.pop() || '';
    const baseName = fileNameParts.join('.');
    const fileName = `${baseName}_${randomSuffix}.${extension}`;
    
    // Append file
    formData.append('File', file);
    
    // Append metadata fields using dot notation for ASP.NET Core model binding
    formData.append('Metadata.FileName', fileName);
    formData.append('Metadata.ContentType', file.type);
    formData.append('Metadata.VirtualPath', 'images');
    formData.append('Metadata.Tags[category]', 'images');
    
    const url = await apiClient.post<string>(this.basePath, formData, {
      requiresAuth: true,
    });
    
    // Backend returns just the URL as a string, convert to MediaItem
    return {
      url: url,
      path: `images/${fileName}`,
      fileName: fileName,
      contentType: file.type,
      virtualPath: 'images',
      tags: { category: 'images' },
    };
  }

  /**
   * Upload multiple media files
   */
  async uploadMultipleMedia(files: File[]): Promise<MediaItem[]> {
    const uploadPromises = files.map(file => this.uploadMedia(file));
    return Promise.all(uploadPromises);
  }

  /**
   * Delete a media file by its account-relative path (MediaItem.path, not the URL)
   */
  async deleteMedia(path: string): Promise<void> {
    // Encode each segment but keep "/" separators: the API route is a catch-all {*path}
    const encodedPath = path.split("/").map(encodeURIComponent).join("/");
    return apiClient.delete(`${this.basePath}/${encodedPath}`, {
      requiresAuth: true,
    });
  }

  /**
   * Generate random 8 character ASCII suffix
   */
  private generateRandomSuffix(): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
}

// Export singleton instance
export const mediaService = new MediaService();
